// fhir-person.js — the shared "read a person off a FHIR resource" helpers.
// Consolidated 2026-08-05 (replatform W2a, extended in W2.1) — these were
// copy-pasted verbatim across a dozen modules (see the per-export notes below).
//
// ALMOST PURE JS ON PURPOSE. lib/shape/* imports this and those shapers must
// stay free of React / DOM / MedplumClient so a Node test can feed them a
// synthetic bundle and so the shapers can lift into a shared package later.
// Keep it that way. The ONE import is `./clinic-time.js`, which is itself
// dependency-free (Intl + Date only) and is already imported by every
// lib/shape/* module — see the age note below for why age needs it.
//
// AGE — ONE formula since 2026-08-05 (owner decision OD4). There used to be
// TWO: `ageOf` sliced the birth YEAR and subtracted ("born 1990 ⇒ 36 all year"),
// while `ageFromBirthDate` was month/day-aware ("born 1990-12-01 ⇒ 35 until
// December"). They disagreed by up to one year for most of the calendar: the
// OPD surfaces (queue/schedule/patients/reports/consult/families/Studio) showed
// the year-slice number while the IPD/ED surfaces (ward, emergency, bedside)
// showed the month/day-aware one. W2a kept them apart because reconciling them
// CHANGES ages on screen — a clinical-display decision, not a refactor. The
// owner made that call on 2026-08-05 (all stored data is demo data): the
// YEAR-SLICE FORMULA IS RETIRED, `ageOf` is deleted, and `ageFromBirthDate` is
// the single age calculation everywhere. OPD-screen ages may therefore read one
// year lower than before for a patient whose birthday has not yet passed this
// year — that is the point; the new number is the correct one.
import { clinicTodayKey } from './clinic-time.js';

/**
 * Display name off any FHIR resource with a `name` HumanName array
 * (Practitioner, Patient, RelatedPerson…). `undefined` — not a placeholder —
 * when the resource has no name, so callers can fall through to their own
 * label. Was copy-pasted in: lib/ipd-live.js, lib/emergency.js,
 * screens/ward/bedside/bedside-glance.js.
 *
 * NOT the same contract as `nameOf` below — see that doc comment.
 */
export function displayNameOf(res) {
  const n = res && res.name && res.name[0];
  if (!n) return undefined;
  return n.text || [...(n.given || []), n.family].filter(Boolean).join(' ') || undefined;
}

/**
 * The OPD shaper's patient name: the SAME HumanName read as `displayNameOf`,
 * but with two deliberate differences that make it a DIFFERENT contract, kept
 * separate on purpose (do not merge the two):
 *   1. it falls back to the literal string 'Patient' instead of `undefined`,
 *      because every OPD row/card must render a label; and
 *   2. it joins the `given` array into ONE element before joining with
 *      `family` — `[given.join(' '), family]` rather than `[...given, family]`
 *      — so a HumanName with an empty-string given part collapses differently.
 * Was copy-pasted in: lib/shape/queue-row.js, lib/shape/encounter-view.js,
 * lib/shape/reports-view.js, lib/shape/schedule-view.js,
 * lib/shape/patients-view.js (identical in all five).
 */
export function nameOf(p) {
  if (!p || !p.name || !p.name[0]) return 'Patient';
  const n = p.name[0];
  return n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' ') || 'Patient';
}

/**
 * Up to two uppercase initials from a display name, for an avatar chip. Empty
 * string when the name yields none — patients-view.js deliberately appends its
 * own `|| '?'` at the call site, so this stays the bare form. Was copy-pasted
 * in: the same five lib/shape/* modules plus lib/medplum-families.js (as
 * `initialsOf` — byte-identical).
 */
export function initials(name) {
  return String(name || '').trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

/**
 * The attending clinician's display name off an Encounter — the participant
 * coded PPRF (primary performer), else the first participant. `null` when the
 * Encounter names nobody. Was copy-pasted in: lib/ipd-live.js,
 * screens/ward/bedside/bedside-glance.js.
 */
export function attendingNameOf(enc) {
  const parts = (enc && enc.participant) || [];
  const pprf = parts.find((pt) => (pt.type || []).some((t) => (t.coding || []).some((c) => c.code === 'PPRF'))) || parts[0];
  const ind = pprf && pprf.individual;
  return (ind && ind.display) || null;
}

/**
 * One-letter sex marker off `Patient.gender` for the "42 · F" identity line.
 * `null` when the resource states no gender (callers all `filter(Boolean)` or
 * render it directly, so a null reads as "not stated").
 *
 * UNIFIED 2026-08-05 (W2.1) from four copies that were NOT identical:
 *   • lib/ipd-live.js + screens/ward/bedside/bedside-glance.js — male→M,
 *     female→F, other→O, anything else → first letter. 'unknown' → 'U'.
 *   • lib/emergency.js — the same PLUS an explicit 'unknown' → '?'.
 *   • lib/medplum-families.js — first letter of whatever `gender` says, and ''
 *     (not null) when absent, so 'unknown' → 'U' and 'other' → 'O'.
 * The emergency variant is the correct one and is what everything now uses:
 * FHIR's `gender` value set is male|female|other|unknown, and 'U' is a
 * misleading marker for "the patient did not state it" — '?' says that plainly.
 * The families call sites both feed `[...].filter(Boolean).join(' · ')`-style
 * renderers, so their '' → null change is invisible.
 */
export function sexOf(p) {
  const g = p && p.gender;
  if (!g) return null;
  return g === 'male' ? 'M' : g === 'female' ? 'F' : g === 'other' ? 'O' : g === 'unknown' ? '?' : String(g).charAt(0).toUpperCase();
}

/**
 * The free-text reason for an Encounter (`reasonCode[0].text`), or `null`.
 * Was copy-pasted — byte-identical — in: lib/ipd-live.js,
 * screens/ward/bedside/bedside-glance.js.
 */
export function reasonTextOf(enc) {
  const rc = enc && enc.reasonCode && enc.reasonCode[0];
  return (rc && rc.text) || null;
}

/**
 * THE age calculation (see the module header): whole years from an ISO
 * birthDate, month/day-aware — the birthday has to have passed. `null` for
 * absent/unparseable input, for a future birthDate, and for an implausible
 * result (≥200 years — the upper sanity bound folded in from
 * lib/medplum-referrals.js's private copy, which is now deleted).
 *
 * BOTH SIDES ARE READ IN THE SAME TIME FRAME, deliberately. A FHIR `date` like
 * "1990-01-01" is parsed by `Date` as UTC midnight, so the previous copies —
 * which compared it against the DEVICE-local `getFullYear()/getMonth()/
 * getDate()` — were mixing frames and could be a year out on a device west of
 * UTC. "Today" here is the CLINIC's calendar day (clinic-time.js, the same
 * source every other date on screen is bucketed by) and the birthDate is read
 * with the UTC getters that match how it was parsed.
 *
 * Was copy-pasted in: lib/ipd-live.js, lib/emergency.js,
 * screens/ward/bedside/bedside-glance.js, lib/medplum-referrals.js (the
 * variant with the <200 bound), and — as the retired `ageOf` year-slice —
 * lib/shape/{encounter,schedule,patients,reports}-view.js,
 * lib/shape/queue-row.js, lib/medplum-families.js, lib/studio-bindings.js.
 */
export function ageFromBirthDate(birthDate) {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return null;
  const [ty, tm, td] = String(clinicTodayKey()).split('-').map(Number);
  if (!ty) return null;
  let age = ty - b.getUTCFullYear();
  const m = tm - (b.getUTCMonth() + 1);
  if (m < 0 || (m === 0 && td < b.getUTCDate())) age -= 1;
  return age >= 0 && age < 200 ? age : null;
}
